import { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, Stack, Typography, Box, Chip, IconButton, Divider } from '@mui/material';
import { useLocales } from '../../locales';
import qrcode from 'qrcode-generator';
import { format } from 'date-fns';
import Iconify from './iconify';
import { LiveClock } from './LiveClock';

export const EntranceCodeDialog = ({ open, onClose, subscription, sellerId, endUser, hideNameAndTime = false }) => {
    const { translate } = useLocales();
    const [qrCodeElementId] = useState(`qr_entrance_${subscription?._id}_${Math.random()}`);

    const generateQRCode = () => {
        if (!subscription || !sellerId) return;
        const qrUrl = `${window.location.origin}/seller-scanner/${sellerId}/${subscription._id}`;
        const qr = qrcode(7, "L");
        qr.addData(qrUrl);
        qr.make();
        const qrElement = document.getElementById(qrCodeElementId);
        if (qrElement) {
            qrElement.innerHTML = qr.createSvgTag(4.5);
        }
    };

    useEffect(() => {
        let timeoutId;
        if (open) {
            timeoutId = setTimeout(() => {
                generateQRCode();
            }, 100);
        }
        return () => clearTimeout(timeoutId);
    }, [open, subscription, sellerId]);

    const fullName = endUser ? `${endUser.fname || ''} ${endUser.lname || ''}`.trim() : null;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1.5 }}>
                <Typography variant="h6" fontWeight="bold">
                    {translate('dashboard.profile_settings.entrance_code_modal_title')}
                </Typography>
                <IconButton onClick={onClose} size="small" aria-label={translate('close')}>
                    <Iconify icon="eva:close-fill" />
                </IconButton>
            </DialogTitle>
            <Divider />

            <DialogContent>
                <Stack spacing={2.5} alignItems="center" sx={{ pt: 2, pb: 2 }}>
                    {/* Subscription name chip */}
                    {subscription?.name && (
                        <Chip
                            label={subscription.name}
                            color="primary"
                            variant="soft"
                            size="medium"
                            sx={{ fontWeight: 600, fontSize: '0.85rem' }}
                        />
                    )}

                    {/* Permanent usage note for this subscription type (set by the seller on the plan).
                        Flat field `seasonTicketUsageTerms`, mirroring `seasonTicketName` elsewhere —
                        NOT `usageTerms`, which only exists on the raw plan document (admin form). */}
                    {subscription?.seasonTicketUsageTerms && (
                        <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ whiteSpace: 'pre-line' }}>
                            {subscription.seasonTicketUsageTerms}
                        </Typography>
                    )}

                    {/* QR Code */}
                    <Box
                        sx={{
                            p: 2.5,
                            borderRadius: 2,
                            bgcolor: 'background.neutral',
                            border: '1px solid',
                            borderColor: 'divider',
                            boxShadow: (theme) => theme.customShadows?.z8 || '0 8px 16px 0 rgba(0,0,0,0.08)',
                        }}
                    >
                        <div id={qrCodeElementId} style={{ lineHeight: 0 }} />
                    </Box>

                    {/* Member info */}
                    {!hideNameAndTime && (
                        <Stack spacing={0.5} alignItems="center">
                            {fullName && (
                                <Typography variant="h6" fontWeight="bold" textAlign="center">
                                    {fullName}
                                </Typography>
                            )}
                            {endUser?.idOrPassport && (
                                <Stack direction="row" spacing={0.5} alignItems="center">
                                    <Typography variant="caption" color="text.disabled" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>
                                        {translate('family.id')}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" fontWeight={500}>
                                        {endUser.idOrPassport}
                                    </Typography>
                                </Stack>
                            )}
                            <LiveClock />
                        </Stack>
                    )}
                </Stack>
            </DialogContent>

            {/* Footer — outside DialogContent so divider spans edge to edge */}
            <Divider />
            <Stack direction="row" spacing={1} alignItems="center" justifyContent="center" sx={{ px: 3, py: 1.5, color: 'text.disabled' }}>
                <Iconify icon="eva:smartphone-outline" width={16} />
                <Typography variant="caption" textAlign="center">
                    {translate('dashboard.profile_settings.entrance_code_description')}
                </Typography>
            </Stack>
        </Dialog>
    );
};
